import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import styled from 'styled-components';
import { Button } from './PageStyles';
import { activityTracker } from '../services/activityTracker';
import { notify } from '../utils/notifications';
import { buildApiUrl } from '../utils/apiClient';
const Container = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 32px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;
const Title = styled.h2 `
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 400;
  margin: 0 0 24px 0;
  letter-spacing: 0.5px;
`;
const UploadSection = styled.div `
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;
const FileInputWrapper = styled.div `
  position: relative;
`;
const FileInput = styled.input `
  display: none;
`;
const FileLabel = styled.label `
  display: inline-block;
  padding: 12px 24px;
  background: transparent;
  border: 1px solid var(--border-color-hover);
  color: var(--text-primary);
  cursor: pointer;
  font-size: 14px;
  letter-spacing: 0.5px;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-tertiary);
    border-color: var(--accent-color);
  }
`;
const FileName = styled.div `
  color: var(--text-secondary);
  font-size: 14px;
  margin-top: 8px;
`;
const UploadButton = styled(Button) `
  align-self: flex-start;
`;
const ProgressSection = styled.div `
  margin: 24px 0;
  padding: 16px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
`;
const ProgressText = styled.div `
  color: var(--text-primary);
  font-size: 14px;
  margin-bottom: 8px;
`;
const ProgressBar = styled.div `
  width: 100%;
  height: 4px;
  background: var(--border-color);
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
    animation: loading 1.5s infinite;
  }

  @keyframes loading {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;
const ResultSection = styled.div `
  margin-top: 24px;
  padding: 20px;
  background: ${props => props.success ? 'rgba(0, 255, 0, 0.05)' : 'rgba(255, 0, 0, 0.05)'};
  border: 1px solid ${props => props.success ? 'rgba(0, 255, 0, 0.2)' : 'rgba(255, 0, 0, 0.2)'};
`;
const ResultTitle = styled.h3 `
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 400;
  margin: 0 0 16px 0;
`;
const ResultStats = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;
const StatItem = styled.div `
  color: var(--text-secondary);
  font-size: 14px;

  strong {
    color: var(--text-primary);
    font-weight: 500;
  }
`;
const TestModeWarning = styled.div `
  background: rgba(255, 165, 0, 0.1);
  border: 1px solid rgba(255, 165, 0, 0.3);
  padding: 12px 16px;
  color: #FFA500;
  font-size: 14px;
  margin-top: 12px;
`;
const ErrorList = styled.div `
  margin-top: 16px;
  max-height: 200px;
  overflow-y: auto;
`;
const ErrorItem = styled.div `
  color: rgba(255, 0, 0, 0.8);
  font-size: 13px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.02);
  margin-bottom: 8px;
  border-left: 2px solid rgba(255, 0, 0, 0.5);
  padding-left: 12px;
`;
export const CSVImporter = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file && file.name.toLowerCase().endsWith('.csv')) {
            setSelectedFile(file);
            setResult(null);
        }
        else {
            notify.warning('Invalid File', 'Please select a CSV file');
        }
    };
    const handleUpload = async () => {
        if (!selectedFile) {
            notify.warning('No File Selected', 'Please select a CSV file first');
            return;
        }
        setUploading(true);
        setResult(null);
        try {
            // Read file as text
            const fileText = await selectedFile.text();
            // Use production API endpoint
            const apiUrl = buildApiUrl('import-rapnet-csv');
            console.log('Uploading to:', apiUrl);
            console.log('File size:', fileText.length, 'bytes');
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'text/csv'
                },
                body: fileText
            });
            console.log('Response status:', response.status, response.statusText);
            console.log('Response headers:', Object.fromEntries(response.headers.entries()));
            // Check if response has content
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Non-JSON response:', text);
                throw new Error(`Server returned non-JSON response (${response.status}): ${text.substring(0, 200)}`);
            }
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || `Import failed with status ${response.status}`);
            }
            setResult(data);
            activityTracker.trackAdminAction('imported-inventory', `Imported ${data.importedCount} diamonds from CSV`, {
                filename: selectedFile.name, importedCount: data.importedCount, failedCount: data.failedCount
            });
            notify.success('Import Complete', `Successfully imported ${data.importedCount} diamonds`);
        }
        catch (error) {
            console.error('Upload error:', error);
            notify.error('Upload Failed', `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
        finally {
            setUploading(false);
        }
    };
    return (_jsxs(Container, { children: [_jsx(Title, { children: "RAPNET CSV Importer" }), _jsxs(UploadSection, { children: [_jsxs(FileInputWrapper, { children: [_jsx(FileInput, { type: "file", id: "csv-file", accept: ".csv", onChange: handleFileChange, disabled: uploading }), _jsx(FileLabel, { htmlFor: "csv-file", children: selectedFile ? 'Change File' : 'Select CSV File' }), selectedFile && (_jsxs(FileName, { children: ["Selected: ", selectedFile.name] }))] }), _jsx(UploadButton, { onClick: handleUpload, disabled: !selectedFile || uploading, children: uploading ? 'Uploading...' : 'Upload & Import' })] }), uploading && (_jsxs(ProgressSection, { children: [_jsx(ProgressText, { children: "Processing CSV and importing to CosmosDB..." }), _jsx(ProgressBar, {})] })), result && (_jsxs(ResultSection, { success: result.success, children: [_jsx(ResultTitle, { children: result.success ? '✅ Import Complete' : '❌ Import Failed' }), _jsxs(ResultStats, { children: [_jsxs(StatItem, { children: [_jsx("strong", { children: result.importedCount }), " diamonds imported"] }), _jsxs(StatItem, { children: [_jsx("strong", { children: result.failedCount }), " failed"] }), _jsxs(StatItem, { children: [_jsx("strong", { children: result.summary.totalRows }), " total rows in CSV"] }), _jsxs(StatItem, { children: [_jsx("strong", { children: result.summary.successfulInserts }), " successful inserts"] })] }), result.errors.length > 0 && (_jsxs(ErrorList, { children: [_jsx(ResultTitle, { children: "Errors:" }), result.errors.map((error, index) => (_jsxs(ErrorItem, { children: ["Row ", error.row, " (Stock: ", error.stockNumber, "): ", error.error] }, index)))] }))] }))] }));
};
